import type { RecommendationView } from "../api/client.js";
import {
  BookmakerBrand,
  bookmakerName,
  sortAuOffers,
} from "./bookmaker-brand.js";

/**
 * An offer tile that is a link when the book published a page, and a plain
 * panel when it did not. Rendering a link either way would put a dead target
 * on the one screen whose whole purpose is choosing where to bet.
 */
function OfferTile({
  className,
  url,
  bookmaker,
  children,
}: {
  readonly className: string;
  readonly url: string | null;
  readonly bookmaker: string;
  readonly children: React.ReactNode;
}) {
  if (url === null)
    return (
      <div className={className} data-testid="au-offer">
        {children}
      </div>
    );
  return (
    <a
      className={`${className} is-link`}
      data-testid="au-offer"
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      title={`Open ${bookmaker}`}
    >
      {children}
    </a>
  );
}

/** Keys that only identify rows (game ids, data provenance) mean nothing on a
 * betting screen. */
const HIDDEN_EVIDENCE = /(ids|provenance)$/i;

const humanise = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Long floats (0.31351351351351353) to three decimals; everything else as is. */
const tidyValue = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "yes" : "no";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return /^-?\d+\.\d{4,}$/.test(text)
    ? String(Number(Number(text).toFixed(3)))
    : text;
};

const percent = (value: unknown): string =>
  typeof value === "number"
    ? `${value >= 0 ? "+" : ""}${(value * 100).toFixed(1)}%`
    : "-";

/**
 * Turn the API's evidence strings into label/value pairs a person can read.
 *
 * Two shapes arrive: "key: value" pairs, and whole JSON objects - one per rule
 * that fired, carrying its calibration bucket. The objects are summarised to
 * the four facts that matter: price band, record in that band, estimated EV
 * and whether it passed the value check.
 */
export function formatEvidence(
  items: readonly string[],
): readonly { readonly label: string; readonly value: string }[] {
  const out: { label: string; value: string }[] = [];
  for (const item of items) {
    if (item.trimStart().startsWith("{")) {
      let parsed: Record<string, unknown> | null;
      try {
        parsed = JSON.parse(item) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      if (parsed !== null && typeof parsed.ruleId === "string") {
        const parts = [
          typeof parsed.bucket === "string" ? `price ${parsed.bucket}` : null,
          typeof parsed.bucketWins === "number" &&
          typeof parsed.bucketLosses === "number"
            ? `${String(parsed.bucketWins)}-${String(parsed.bucketLosses)} in band`
            : null,
          `EV ${percent(parsed.estimatedEv)}`,
          parsed.valuePass === true ? "value pass" : "no value",
        ];
        out.push({
          label: parsed.ruleId,
          value: parts.filter((part) => part !== null).join(" · "),
        });
        continue;
      }
      if (parsed !== null) {
        for (const [key, value] of Object.entries(parsed))
          if (
            !HIDDEN_EVIDENCE.test(key) &&
            (value === null || typeof value !== "object")
          )
            out.push({ label: humanise(key), value: tidyValue(value) });
        continue;
      }
    }
    const split = item.indexOf(": ");
    if (split < 0) {
      out.push({ label: "", value: item });
      continue;
    }
    const key = item.slice(0, split);
    if (HIDDEN_EVIDENCE.test(key)) continue;
    out.push({ label: humanise(key), value: tidyValue(item.slice(split + 2)) });
  }
  return out;
}

export function OfferDrawer({
  recommendation,
}: {
  recommendation: RecommendationView | null;
}) {
  if (recommendation === null)
    return (
      <aside className="offer-panel card">
        <p className="muted">Select a signal to inspect offers and evidence.</p>
      </aside>
    );
  const au = sortAuOffers(
    recommendation.offers.filter((offer) => offer.role === "AU_EXECUTION"),
  );
  // DraftKings and, for NBA from 25 Oct 2026, Pinnacle: one row each.
  const references = sortAuOffers(
    recommendation.offers.filter((offer) => offer.role === "US_REFERENCE"),
  );
  const bestPrice = Math.max(...au.map((offer) => offer.price));
  const evidence = formatEvidence(recommendation.evidence);
  return (
    <aside className="offer-panel card" aria-label="Offer comparison">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Exact offer comparison</span>
          <h3>{recommendation.description}</h3>
        </div>
        <span className="status-badge">
          {recommendation.status.replaceAll("_", " ")}
        </span>
      </div>
      <h4>
        Australian bookmakers <span>Executable</span>
      </h4>
      <div
        className="offer-grid"
        role="region"
        aria-label="Australian bookmaker offers, best price first"
      >
        {au.map((offer, index) => (
          <OfferTile
            className={`offer-tile ${offer.price === bestPrice ? "best" : ""}`}
            url={offer.url}
            bookmaker={offer.bookmaker}
            key={`${offer.bookmaker}|${String(offer.line)}`}
          >
            <BookmakerBrand bookmaker={offer.bookmaker} />
            {index === 0 ? <span className="best-label">Best</span> : null}
            <strong data-testid="offer-price">{offer.price.toFixed(2)}</strong>
            <small>
              {offer.line === null
                ? "H2H"
                : offer.product === "MILESTONE"
                  ? `${String(Math.ceil(offer.line))}+ milestone`
                  : `Line ${String(offer.line)}`}
            </small>
            <small
              className={
                recommendation.executionMode === "TRACK_ONLY" ||
                offer.price >= recommendation.minimumOdds
                  ? "offer-value positive"
                  : "offer-value negative"
              }
            >
              {recommendation.executionMode === "TRACK_ONLY"
                ? "Track"
                : offer.price >= recommendation.minimumOdds
                  ? "Bet"
                  : "Below min"}
            </small>
          </OfferTile>
        ))}
      </div>
      {/* Most sports have no US reference at all; an empty box only adds noise. */}
      {references.length === 0 ? null : (
        <section className="reference-card">
          <h4>Reference books — never executable</h4>
          {references.map((reference) => (
            <div
              className="reference-row"
              key={`${reference.bookmaker}|${String(reference.line)}`}
            >
              <span className="reference-bookmaker">
                <BookmakerBrand bookmaker={reference.bookmaker} reference />
                <span>
                  {bookmakerName(reference.bookmaker)} · Reference only
                  {reference.line === null
                    ? ""
                    : ` · Line ${String(reference.line)}`}
                </span>
              </span>
              <strong>{reference.price.toFixed(2)}</strong>
            </div>
          ))}
        </section>
      )}
      {evidence.length === 0 ? null : (
        <section className="evidence">
          <h4>Why it qualified</h4>
          <ul>
            {evidence.map((item, index) => (
              <li key={`${item.label}|${String(index)}`}>
                {item.label === "" ? null : <span>{item.label}</span>}
                <strong>{item.value}</strong>
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
}
